import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import 'bulma/css/bulma.min.css'
import './Workouts.css'

import { useAuth } from './hooks/useAuth'
import { useSession } from './hooks/useSession'

function WorkoutsNew() {
  const navigate = useNavigate()
  const { user, dbUser, loading: authLoading } = useAuth()
  const { createSession, loading: createLoading, error: createError } = useSession()
  
  const [formData, setFormData] = useState({
    title: '',
    session_type: '',
    session_date: '',
    start_time: '',
    duration_minutes: 135,
    location: 'RMT (Rochester Muay Thai)',
    coach_name: 'Coach Christian',
    notes: 'Student vans will pick people up. Contact us via Slack: muaythaibeats.slack.com'
  })
  
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Check authorization
  if (authLoading) {
    return (
      <div className="workouts-container">
        <div className="has-text-centered">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!user || dbUser?.is_admin !== true) {
    return (
      <div className="workouts-container">
        <div className="notification is-danger">
          <h2 className="title is-4">Unauthorized</h2>
          <p>You do not have permission to access this page.</p>
          <Link to="/workouts" className="button is-primary">
            Back to Sessions
          </Link>
        </div>
      </div>
    )
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (error) setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)
    setIsSubmitting(true)

    try {
      // Validate required fields
      if (!formData.title || !formData.session_type || !formData.session_date || 
          !formData.start_time || !formData.location || !formData.coach_name) {
        setError('Please fill in all required fields.')
        setIsSubmitting(false)
        return
      }

      const result = await createSession(formData)
      
      if (result.success) {
        // Show success message briefly before redirecting
        setSuccessMessage('Session created successfully!')
        setTimeout(() => {
          navigate('/workouts')
        }, 1500)
      } else {
        setError(result.error || 'Failed to create session. Please try again.')
        setIsSubmitting(false)
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.')
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/workouts')
  }

  return (
    <div className="workouts-container">
      <div className="level">
        <div className="level-left">
          <div>
            <h1 className="title">Add New Workout</h1>
            <p className="subtitle">Create a new training session</p>
          </div>
        </div>
        <div className="level-right">
          <Link to="/workouts" className="button">
            Back to Sessions
          </Link>
        </div>
      </div>

      {/* Success notification */}
      {successMessage && (
        <div className="notification is-success">
          <button className="delete" onClick={() => { setSuccessMessage(null) }}></button>
          {successMessage}
        </div>
      )}

      {/* Error notification */}
      {(error || createError) && (
        <div className="notification is-danger">
          <button className="delete" onClick={() => { setError(null) }}></button>
          {error || createError}
        </div>
      )}

      {/* Form */}
      <div className="box">
        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="field">
            <label className="label">Title <span className="has-text-danger">*</span></label>
            <div className="control">
              <input
                className="input"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Morning Training Session"
                required
              />
            </div>
          </div>

          {/* Session Type */}
          <div className="field">
            <label className="label">Session Type <span className="has-text-danger">*</span></label>
            <div className="control">
              <div className="select is-fullwidth">
                <select
                  name="session_type"
                  value={formData.session_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select session type...</option>
                  <option value="main">Main</option>
                  <option value="beginner">Beginner</option>
                  <option value="women">Women</option>
                </select>
              </div>
            </div>
          </div>

          {/* Date and Time Row */}
          <div className="columns">
            <div className="column">
              <div className="field">
                <label className="label">Session Date <span className="has-text-danger">*</span></label>
                <div className="control">
                  <input
                    className="input"
                    type="date"
                    name="session_date"
                    value={formData.session_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>
            <div className="column">
              <div className="field">
                <label className="label">Start Time <span className="has-text-danger">*</span></label>
                <div className="control">
                  <input
                    className="input"
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>
            <div className="column">
              <div className="field">
                <label className="label">Duration (minutes)</label>
                <div className="control">
                  <input
                    className="input"
                    type="number"
                    name="duration_minutes"
                    value={formData.duration_minutes}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="135"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="field">
            <label className="label">Location <span className="has-text-danger">*</span></label>
            <div className="control">
              <input
                className="input"
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* Coach Name */}
          <div className="field">
            <label className="label">Coach Name <span className="has-text-danger">*</span></label>
            <div className="control">
              <input
                className="input"
                type="text"
                name="coach_name"
                value={formData.coach_name}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* Notes */}
          <div className="field">
            <label className="label">Notes</label>
            <div className="control">
              <textarea
                className="textarea"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
                placeholder="Additional notes about the session..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="field is-grouped">
            <div className="control">
              <button
                type="submit"
                className={`button is-primary ${isSubmitting || createLoading ? 'is-loading' : ''}`}
                disabled={isSubmitting || createLoading}
              >
                Create Session
              </button>
            </div>
            <div className="control">
              <button
                type="button"
                className="button is-light"
                onClick={handleCancel}
                disabled={isSubmitting || createLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default WorkoutsNew
